import { Field, FieldProps } from 'react-final-form';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { ErrorContainer, ErrorStyle } from './Error';

import { OptionCheckbox } from 'components/Select/icons/option_checkbox';
import { SelectedOptionCheckbox } from 'components/Select/icons/selected_option_checkbox';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const StyledLabel = styled.div<{ hasError: boolean; errorStyle: ErrorStyle }>`
  font-size: 16px;
  font-weight: 500;
  line-height: 18.75px;
  color: ${props =>
    props.hasError && props.errorStyle === 'default' ? 'rgba(175, 41, 48, 1)' : 'rgba(117, 117, 117, 1)'};
`;

const StyledSelect = styled(Select)<{ hasError: boolean; includeDelimiter: boolean; errorStyle?: ErrorStyle }>`
  & > div {
    background: #f5f5f5;
  }

  & .contained_form_select__value-container {
    padding: 14.5px 12px 13.5px 12px;
    outline: none;
    min-height: 18px;
  }

  & .contained_form_select__control {
    border-radius: 0;
    border: ${props => (props.hasError ? '1px solid rgba(175, 41, 48, 1)' : 'none')};
  }

  & .contained_form_select__control:hover {
    border: ${props => (props.hasError ? '1px solid rgba(175, 41, 48, 1)' : 'none')};
    border-radius: 0;
  }

  & .contained_form_select__control--is-focused {
    border: ${props => (props.hasError ? '1px solid rgba(175, 41, 48, 1)' : 'none')};
    border-radius: 0;
    box-shadow: none;
  }

  & .contained_form_select__dropdown-indicator {
    padding: 8px;
  }

  & .contained_form_select__control {
    min-height: unset;
  }

  & .contained_form_select__menu-list {
    padding: 4px 4px 4px 0;
    max-height: 180px;
  }

  & .contained_form_select__option--is-focused {
    background-color: rgba(0, 0, 0, 0.14) !important;
  }

  & .contained_form_select__value-container div:last-child {
    margin: 0;
    padding: 0;
  }

  & .contained_form_select__indicator-separator {
    display: ${props => (props.includeDelimiter ? 'block' : 'none')};
  }

  & .contained_form_select__dropdown-indicator,
  .contained_form_select__dropdown-indicator:hover {
    color: hsl(0, 0%, 40%);
  }

  & .contained_form_select__dropdown-indicator {
    color: hsl(0, 0%, 40%);
  }

  & .contained_form_select__dropdown-indicator svg {
    width: 24px;
    height: 24px;
  }

  & .contained_form_select__option--is-selected {
    font-weight: 500;
    background-color: #fff !important;
    color: inherit !important;
  }

  & .contained_form_select__single-value {
    font-weight: 500;
    color: #000;
    font-size: 18px;
    line-height: 21px;
  }

  & .contained_form_select__control--is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  & .contained_form_select__placeholder {
    color: #808080;
    margin: 0 2px;
    font-size: 16px;
    line-height: 18px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
  }

  ${({ hasError, errorStyle }) =>
    hasError &&
    errorStyle === 'filled' &&
    `
      & .contained_form_select__placeholder {
        color: #e93323;
        font-weight: 500;
        opacity: 1;
        font-size: 12px;
        line-height: 21px;
      }

      & .contained_form_select__value-container {
        outline: none;
      }
    `}
`;

type Props = {
  name: string;
  options: Array<{ label: string; value: any }>;
  label?: string;
  includeDelimiter?: boolean;
  thickArrow?: boolean;
  disabled?: boolean;
  placeholder?: string;
  errorStyle?: 'default' | 'filled';
  isMulti?: boolean;
  menuPosition?: 'fixed' | 'absolute';
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  afterSelect?: (data: any) => void;
  AdditionalComponent?: () => JSX.Element;
  instantError?: boolean;
  isSearchable?: boolean;
} & FieldProps<any, any>;

const DisplayValue = styled.div`
  max-width: 154px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MultipleOption = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
`;

const MenuOptionText = styled.span`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Placeholder = styled.span`
  font-family: Roboto;
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #757575;
`;

const Option = ({ children, ...props }: any) => {
  if (!props.isMulti) {
    return (
      <components.Option {...props}>
        <MenuOptionText>{children}</MenuOptionText>
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <MultipleOption>
        <div style={{ display: 'flex' }}>{props.isSelected ? <SelectedOptionCheckbox /> : <OptionCheckbox />}</div>
        <MenuOptionText>{children}</MenuOptionText>
      </MultipleOption>
    </components.Option>
  );
};

const ValueContainer = (props: any) => {
  const selectedOptions = props.selectProps.value;

  const displayValue =
    props.isMulti && Array.isArray(selectedOptions)
      ? selectedOptions?.map((option: any) => option.label)?.join(', ') || ''
      : selectedOptions?.label || '';

  return (
    <components.ValueContainer {...props}>
      {props.hasValue ? (
        <DisplayValue className="contained_form_select__single-value">{displayValue}</DisplayValue>
      ) : (
        <Placeholder className="contained_form_select__placeholder">
          {props.selectProps.placeholder || 'Select value'}
        </Placeholder>
      )}
    </components.ValueContainer>
  );
};

export const ContainedFormReactSelect = ({
  name,
  options,
  label,
  includeDelimiter = true,
  thickArrow = false,
  disabled = false,
  placeholder,
  errorStyle = 'default',
  menuPosition = 'fixed',
  isMulti = false,
  AdditionalComponent,
  onMenuClose,
  onMenuOpen,
  afterSelect,
  instantError = false,
  isSearchable = true,
  ...finalFormProps
}: Props) => {
  return (
    <Field name={name} {...finalFormProps}>
      {props => {
        console.log(props.meta, name, 'meta + name');
        const hasError = (instantError || props.meta.dirty) && props.meta.error;

        const handleChange = (e: any) => {
          props.input.onChange(e);
          if (afterSelect) afterSelect(e);
        };

        return (
          <Wrapper>
            {label && (
              <StyledLabel className="contained_input_label" errorStyle={errorStyle} hasError={hasError}>
                {label}
              </StyledLabel>
            )}
            <StyledSelect
              components={{
                DropdownIndicator: thickArrow ? ThickArrow : components.DropdownIndicator,
                Option: isMulti ? Option : components.Option,
                ValueContainer: isMulti ? ValueContainer : components.ValueContainer
              }}
              includeDelimiter={includeDelimiter}
              closeMenuOnScroll={false}
              hasError={hasError}
              isMulti={isMulti}
              errorStyle={errorStyle}
              classNamePrefix="contained_form_select"
              value={props.input.value}
              onChange={handleChange}
              autoFocus={false}
              options={options}
              isDisabled={disabled}
              placeholder={placeholder}
              onBluer={props.input.onBlur}
              onFocus={props.input.onFocus}
              onMenuOpen={onMenuOpen}
              onMenuClose={onMenuClose}
              isSearchable={isSearchable}
              menuPosition={menuPosition}
            />
            {hasError && <ErrorContainer errorStyle={errorStyle}>{props.meta.error}</ErrorContainer>}
            {!hasError && AdditionalComponent && <AdditionalComponent />}
          </Wrapper>
        );
      }}
    </Field>
  );
};

const TriangleArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M11.9996 15.6L5.97461 9.60001H18.0246L11.9996 15.6Z" fill="#757575" />
  </svg>
);

export const ThickArrow = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <TriangleArrow />
    </components.DropdownIndicator>
  );
};
